<template>
  <v-dialog
    content-class="add-asset-popup"
    :value="internalValue"
    :width="width"
    @click:outside="onEscapeClicked"
    @keydown.esc="onEscapeClicked"
  >
    <v-card class="popup-wrapper" >
      <span class="escape-link d-inline-block" @click="onEscapeClicked"> X {{ $t('newProject.esc') }} </span>
      <div class="content">
        <div class="add-title">
          {{ formattedTitle }}
        </div>
        <el-radio-group v-model="activeTab" size="small" v-if='!isSummaryTab'>
          <el-radio-button
            class="tab-button"
            v-for="tab in tabs"
            :label="tab.label"
            :key="tab.label"
          />
        </el-radio-group>
        <div v-if='isSubdomainTab'>
          <div class="add-item-form" :style="`width: ${width}`">
            <div class="subdomain-row" v-for="(value, index) in subdomainItems" :key="index">
              <div class="item-inputs">
                <div class="text-field-section">
                  <text-field
                    :ref="`url_${index}`"
                    type="text"
                    v-model="subdomainItems[index].url"
                    class="login"
                    :rules="validationRules"
                    :label="`${$t('configuration.subdomain.label')} #${index+1}`"
                    :placeholder="$t('configuration.infrastructure.add.asset.subdomain.placeholder')"
                  />
                </div>
                <span class='dot-for-subdomain'>.</span>
                <div class='choose-tld-row-section-for-subdomains'>
                  <span class="choose-tld-row-section-for-subdomains__title">{{ $t("configuration.add.url.associate.tld") }}</span>
                  <v-autocomplete
                    class-name="domain-select"
                    :placeholder="$t('select')"
                    :items='listOfDomains'
                    :append-icon="'expand_more'"
                    v-model='subdomainItems[index].tld'
                    :rules="[v => !!v || $t('configuration.add.url.associate.tld.required')]"
                    dense
                    outlined
                    autofocus
                    return-object
                  />
                </div>
                <div class='delete-row-section'>
                  <custom-tooltip class='delete-row-section__tooltip' placement="right" popper-class="light-tooltip">
                    <div slot='buttonContent'>
                      <span
                        class="delete-icon material-icons"
                        @click="deleteCurrentItem(index)"
                      >
                          highlight_off
                      </span>
                    </div>
                    <div slot="content">
                      <slot name="tooltip">
                        {{$t('delete')}}
                      </slot>
                    </div>
                  </custom-tooltip>
                </div>
              </div>
              <div class="subdomain-row__hint-wrap" :class="{ 'subdomain-row__hint-wrap--open': isSubdomainRowFilled(index) }">
                <div class="subdomain-row__hint">
                  <span
                    v-if="isSubdomainRowFilled(index) && subdomainRowError(index)"
                    class="subdomain-row__hint--error"
                  >
                    <span class="material-icons subdomain-row__hint-icon">error_outline</span>
                    <span>{{ subdomainRowError(index) }}</span>
                  </span>
                  <span
                    v-else-if="isSubdomainRowFilled(index)"
                    class="subdomain-row__hint--preview"
                  >
                    <span class="material-icons subdomain-row__hint-icon">check_circle</span>
                    <span>{{ $t('configuration.add.asset.subdomain.preview') }}</span>
                    <strong class="subdomain-row__hint-asset">{{ subdomainPreview(index) }}</strong>
                  </span>
                </div>
              </div>
            </div>
            <div class="add-new-item-section">
              <div class="add-new-item-header">
                <label  class="add-new-item-add-button"  @click="addItem">
                  <span class="plus">+</span>{{ $t('add.new.email.popup.button') }}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if='isHostsTab'>
          <div class="add-item-form" :style="`width: ${width}`">
            <div class='error' v-if='showWarning'> {{ $t('add.new.host.warning') }} </div>
            <div class="item-inputs" v-for="(value, index) in hostItems" :key="index">
              <div class="text-field-section hosts">
                <text-field
                  :ref="`host_${index}`"
                  type="text"
                  v-model="hostItems[index].url"
                  class="login"
                  :rules="validationRules"
                  :label="`${label} #${index+1}`"
                  :placeholder="$t('configuration.infrastructure.add.asset.host.placeholder')"
                />
              </div>
              <div class='choose-tld-row-section-for-hosts'>
                <span class="choose-tld-row-section-for-hosts__title">{{ tldForHostLabel }}</span>
                <v-autocomplete
                  class-name="domain-select"
                  :placeholder="$t('select')"
                  :items='listOfDomains'
                  :append-icon="'expand_more'"
                  v-model='hostItems[index].tld'
                  dense
                  outlined
                  autofocus
                  return-object
                  @change='loadSubdomains(hostItems[index].tld, index)'
                />
              </div>
              <div class='choose-subdomain-row-section'>
                <span class="choose-subdomain-row-section__title">{{ $t('configuration.add.url.associate.subdomain.host') }}</span>
                <v-autocomplete
                  class-name="domain-select"
                  :placeholder="$t('select')"
                  :items='hostItems[index].subdomainsOptions.length > 0 ? listOfSubdomains(index) : []'
                  :loading='loadingSubdomains && loadingIndex === index'
                  :append-icon="'expand_more'"
                  v-model='hostItems[index].subdomain'
                  dense
                  outlined
                  autofocus
                  return-object
                />
              </div>
              <div class='delete-row-section'>
                <custom-tooltip class='delete-row-section__tooltip' placement="right" popper-class="light-tooltip">
                  <div slot='buttonContent'>
                    <span
                      class="delete-icon material-icons"
                      @click="deleteCurrentItem(index)"
                    >
                        highlight_off
                    </span>
                  </div>
                  <div slot="content">
                    <slot name="tooltip">
                      {{$t('delete')}}
                    </slot>
                  </div>
                </custom-tooltip>
              </div>
            </div>
            <div class="add-new-item-section">
              <div class="add-new-item-header">
                <label  class="add-new-item-add-button"  @click="addItem">
                  <span class="plus">+</span>{{ $t('add.new.email.popup.button') }}
                </label>
              </div>
            </div>
            <custom-checkbox
              class='run-ip-resolver-checkbox'
              v-model="runIpResolver"
              size="s"
              :title="$t('configuration.add.asset.popup.run.ip.resolver').toString()"
              color="cold-marine"
            />
          </div>
        </div>
        <div v-else-if='isCSVTab' class='add-asset-popup__csv'>
          <div class='add-asset-popup__csv__wrapper'>
            <v-alert
              text
              dense
              color="teal"
              icon="info"
              border="left"
              class='add-asset-popup__csv__wrapper__message'
            >
              {{$t('configuration.infrastructure.add.asset.title.csv.message')}}
            </v-alert>
            <custom-tooltip popper-class="light-tooltip">
              <div slot='buttonContent'>
                <download-c-s-v class="download-csv-btn" @click="downloadCSVReport"/>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  Sample CSV
                </slot>
              </div>
            </custom-tooltip>
          </div>
          <div class='add-asset-popup__csv__selection'>
            <v-file-input
              accept=".csv"
              v-model='csvFile'
              class='add-asset-popup__csv__selection__input-csv'
              :label="$t('configuration.infrastructure.add.asset.title.csv.label')"
              :placeholder="$t('configuration.infrastructure.add.asset.title.csv.placeholder')"
              :show-size="1000"
            ></v-file-input>
            <div class='add-asset-popup__csv__selection__tld'>
              <span class="add-asset-popup__csv__selection__tld__title">{{ tldForCSVLabel }}</span>
              <v-autocomplete
                class-name="add-asset-popup__csv__selection__tld__domain-select"
                :placeholder="$t('select')"
                :items='listOfDomains'
                :append-icon="'expand_more'"
                v-model='tldForCsv'
                dense
                outlined
                autofocus
                return-object
              />
            </div>
          </div>
          <custom-checkbox
            class='run-ip-resolver-checkbox'
            v-model="runIpResolverForCSV"
            size="s"
            :title="$t('configuration.add.asset.popup.run.ip.resolver').toString()"
            color="cold-marine"
          />
        </div>
        <div v-else>
          <div class='summary-items' v-if='!isSubdomainsSummaryEmpty'>
            <div class='summary-item-row'>
              <div class='row-title'> {{ $t('configuration.infrastructure.add.asset.title.subdomain') }}</div>
              <div class='row-title'> {{ $t('configuration.infrastructure.add.asset.title.tld') }} </div>
            </div>
            <v-divider class='title-divider'/>
            <div class='summary-item-row' v-for='(item, index) in subdomainItems' :key='index'>
              <div class='row-value'> {{ item.url }}</div>
              <div class='row-value'> {{ item.tld }} </div>
            </div>
          </div>
          <div class='summary-items' v-if='!isHostsSummaryEmpty'>
            <div class='summary-item-row'>
              <div class='row-title'> {{ $t('configuration.infrastructure.add.asset.title.host') }}</div>
              <div class='row-title'> {{ $t('configuration.infrastructure.add.asset.title.tld') }} </div>
              <div class='row-title'> {{ $t('configuration.infrastructure.add.asset.title.subdomain') }} </div>
            </div>
            <v-divider class='title-divider'/>
            <div class='summary-item-row' v-for='(item, index) in hostItems' :key='index'>
              <div class='row-value'> {{ item.url }}</div>
              <div class='row-value'> {{ item.tld }} </div>
              <div class='row-value'> {{ item.subdomain }} </div>
            </div>
          </div>
          <div class='summary-items' v-if='!isCsvSummaryEmpty'>
            <div class='summary-item-row'>
              <div class='row-title'> {{ $t('configuration.infrastructure.add.asset.title.csv.row.title') }}</div>
              <div class='row-title'> {{ $t('configuration.infrastructure.add.asset.title.tld') }} </div>
            </div>
            <v-divider class='title-divider'/>
            <div class='summary-item-row'>
              <div class='row-value'> {{ csvFile.name }}</div>
              <div class='row-value'> {{ tldForCsv }} </div>
            </div>
          </div>
          <div class="buttons">
            <v-btn small depressed class="back-button btn-secondary" @click="onBackClick"> {{ $t('confirm.back') }} </v-btn>
            <v-btn small depressed class="save-button btn-primary" :disabled="saveButtonDisabled" @click="onSaveClick">
              {{ addButtonTitle }}
            </v-btn>
          </div>
        </div>
      </div>
      <div class="buttons" v-if='!isSummaryTab'>
        <v-btn small depressed class="next-button btn-primary" :disabled='saveButtonDisabled' @click="onClickNext">
          {{ $t('configuration.infrastructure.add.asset.next.button') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { rules, buildSubdomainAsset, isSubdomainAssetValid } from '@/utils/validationUtils';
import { CONFIGURATION } from '@/constants/configuration';
import { GET_SUBDOMAINS_FOR_TLD, REMOVE_SUBDOMAINS_FOR_TLD } from '@/store/project/actionTypes';
import DownloadCSV from '@/assets/icons/download_csv.svg?inline';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import TextField from '../common/TextField.vue';

export default {
  name: 'AddAssetPopup',
  components: {
    CustomCheckbox, TextField, DownloadCSV, CustomTooltip,
  },
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: () => '',
    },
    placeholder: {
      type: String,
      default: () => '',
    },
    addButtonTitle: {
      type: String,
      default: () => '',
    },
    assets: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loadingSubdomains: false,
      loadingIndex: 0,
      urlForSubdomain: [],
      isSubdomainsFormValid: false,
      isHostsFormValid: false,
      formattedTitle: this.title,
      showSummary: false,
      tldForCsv: null,
      subdomainItems: [{ url: '', tld: '', subdomain: '' }],
      hostItems: [{
        url: '', tld: '', subdomain: '', subdomainsOptions: [],
      }],
      runIpResolver: false,
      runIpResolverForCSV: false,
      csvFile: new File([], ''),
      tab: this.$t('configuration.infrastructure.assets.subdomain'),
    };
  },
  computed: {
    ...mapGetters(['subdomainsForTld', 'domains', 'configurationDomains', 'configurationSubdomains', 'organizationFilter']),
    tldForHostLabel() {
      return this.runIpResolver ? this.$t('configuration.add.url.associate.tld.host') : this.$t('configuration.add.url.associate.tld.host').split('(Optional)')[1];
    },
    tldForCSVLabel() {
      return this.runIpResolverForCSV ? this.$t('configuration.add.url.associate.tld.csv') : this.$t('configuration.add.url.associate.tld.csv').split('(Optional)')[1];
    },
    isHostsSummaryEmpty() {
      return this.hostItems.length === 1 && (!this.hostItems[0].url);
    },
    isCsvSummaryEmpty() {
      return this.csvFile === null;
    },
    isSubdomainsSummaryEmpty() {
      return this.subdomainItems.length === 1 && (!this.subdomainItems[0].tld || !this.subdomainItems[0].url);
    },
    validationRulesOptions() {
      return {
        ...rules,
        hostWithRange: v => (rules.hostWithRange(v) || rules.host(v) || (rules.hostWithHyphenRange(v) && rules.hostRangeValid(v)) || this.$t('configuration.should.be.host')),
        subdomain: v => rules.subdomain(v),
      };
    },
    validationRules() {
      return this.isSubdomainTab ? this.subdomainRules : this.hostRules;
    },
    hostRules() {
      return [this.validationRulesOptions.hostWithRange];
    },
    subdomainRules() {
      return [this.validationRulesOptions.subdomain];
    },
    isSubdomainTab() {
      return this.activeTab === this.subdomainTab.label;
    },
    isHostsTab() {
      return this.activeTab === this.hostTab.label;
    },
    isCSVTab() {
      return this.activeTab === this.csvTab.label;
    },
    isSummaryTab() {
      return this.activeTab === this.summaryTab.label;
    },
    subdomainTab() {
      return {
        label: this.$t('configuration.infrastructure.assets.subdomain'),
      };
    },
    hostTab() {
      return {
        label: this.$t('configuration.infrastructure.add.asset.title.host'),
      };
    },
    csvTab() {
      return {
        label: this.$t('configuration.infrastructure.add.asset.title.csv'),
      };
    },
    summaryTab() {
      return {
        label: this.$t('configuration.infrastructure.assets.summary'),
      };
    },
    tabs() {
      return [this.subdomainTab, this.hostTab, this.csvTab];
    },
    activeTab: {
      set(tab) {
        this.tab = tab;
      },
      get() {
        return this.tab;
      },
    },
    width() {
      if (this.isSubdomainTab) {
        return 680;
      }
      if (this.isCSVTab) {
        return 690;
      }
      return 960;
    },
    showWarning() {
      return (this.isSubdomainTab && !this.isSubdomainsFormValid) || (this.isHostsTab && !this.isHostsFormValid);
    },
    saveButtonDisabled() {
      // On the subdomain tab the TLD association is required, so gate strictly on subdomain
      // validity AND on there being something to submit (every row needs a prefix and a TLD),
      // so Next is disabled in the empty/neutral state instead of leading to an empty review.
      if (this.isSubdomainTab) {
        const allRowsComplete = this.subdomainItems.every(item => item.url && item.url.trim() && item.tld);
        return !allRowsComplete || !this.isSubdomainsFormValid;
      }
      return !this.isHostsFormValid && !this.isSubdomainsFormValid && ((this.csvFile !== null && (this.csvFile.size === 0 || (!this.runIpResolverForCSV && !this.tldForCsv))) || this.csvFile === null);
    },
    listOfDomains() {
      return this.configurationDomains.filter(item => this.organizationFilter.includes(item.organization.name)).map(domain => domain.configData).sort();
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  watch: {
    runIpResolver() {
      this.hostItems = [...this.hostItems];
    },
    subdomainsForTld: {
      handler() {
        const relevantManualConfigurationSubdomains =
          this.configurationSubdomains
            .filter(subdomain => subdomain.tld === this.hostItems[this.loadingIndex].tld)
            .map(subdomainObj => subdomainObj.configData);
        this.loadingSubdomains = false;
        this.hostItems[this.loadingIndex].subdomainsOptions = [...this.subdomainsForTld, ...relevantManualConfigurationSubdomains];
      },
      immediate: true,
      deep: true,
    },
    async internalValue(value) {
      await this.clearSubdomains();
      if (value) {
        this.init();
      }
    },
    subdomainItems: {
      handler() {
        // Neutral state: a single empty row, nothing entered yet.
        if (this.subdomainItems.length === 1 && this.subdomainItems[0].url.trim() === '') {
          this.isSubdomainsFormValid = true;
          return;
        }
        let allValid = true;
        for (let i = 0; i < this.subdomainItems.length; i += 1) {
          const { url, tld } = this.subdomainItems[i];
          this.urlForSubdomain[i] = buildSubdomainAsset(url, tld);
          if (!isSubdomainAssetValid(url, tld)) {
            allValid = false;
          }
        }
        this.isSubdomainsFormValid = allValid;
      },
      deep: true,
    },
    hostItems: {
      handler() {
        if (this.hostItems.length === 1 && this.hostItems[0].url === '' && this.hostItems[0].subdomain === '' && this.hostItems[0].tld === '') {
          this.isHostsFormValid = true;
        } else {
          const validHostsArray = [];
          let validHostsAssociation = false;
          for (let i = 0; i < this.hostItems.length; i += 1) {
            if (this.hostItems[i]) {
              const validationForCurrentRef = this.hostRules?.map(validationRule => {
                let result = false;
                if (this.hostItems[i].url !== '') {
                  result = validationRule(this.hostItems[i].url);
                }
                return result;
              });
              const booleanValue = validationForCurrentRef.every(e => ((typeof (e) === 'boolean') && (e === true)));
              validHostsArray.push(booleanValue);
            }
          }
          if (this.hostItems.length > 1 || this.hostItems[0].url !== '') {
            validHostsAssociation = this.hostItems.every(item => item.url);
          }
          this.isHostsFormValid = !validHostsArray.includes(false) && validHostsAssociation;
        }
      },
      deep: true,
    },
  },
  methods: {
    // A subdomain row has enough to preview/validate once it has a prefix and a TLD.
    isSubdomainRowFilled(index) {
      const item = this.subdomainItems[index];
      return !!(item && item.url && item.url.trim() && item.tld);
    },
    // Full asset that will be created for this row (prefix joined with its TLD).
    subdomainPreview(index) {
      const item = this.subdomainItems[index];
      if (!item || !item.url || !item.tld) return '';
      return buildSubdomainAsset(item.url, item.tld);
    },
    // Row-level error when the typed value is not a subdomain of the selected TLD.
    subdomainRowError(index) {
      const item = this.subdomainItems[index];
      if (!item || !item.url || !item.tld) return '';
      if (isSubdomainAssetValid(item.url, item.tld)) return '';
      return this.$t('configuration.add.asset.subdomain.not.under.tld', {
        subdomain: item.url.trim(),
        tld: item.tld,
      });
    },
    listOfSubdomains(index) {
      const subdomainSet = new Set(this.hostItems[index]?.subdomainsOptions);
      return Array.from(subdomainSet).sort();
    },
    async loadSubdomains(tld, index) {
      this.loadingSubdomains = true;
      this.loadingIndex = index;
      this.hostItems[this.loadingIndex].subdomain = '';
      await this.$store.dispatch(GET_SUBDOMAINS_FOR_TLD, { tld });
    },
    async clearSubdomains() {
      await this.$store.dispatch(REMOVE_SUBDOMAINS_FOR_TLD);
    },
    onEscapeClicked() {
      this.init();
      this.internalValue = false;
    },
    init() {
      this.isHostsFormValid = true;
      this.isSubdomainsFormValid = true;
      this.formattedTitle = this.title;
      this.showSummary = false;
      this.activeTab = this.subdomainTab.label;
      this.subdomainItems = [{ url: '', tld: '', subdomain: '' }];
      this.hostItems = [{
        url: '', tld: '', subdomain: '', subdomainsOptions: [],
      }];
      this.csvFile = null;
    },
    onCloseClick() {
      this.init();
      this.internalValue = false;
    },
    onBackClick() {
      this.formattedTitle = this.title;
      this.activeTab = this.subdomainTab.label;
      for (let i = 0; i < this.subdomainItems.length; i += 1) {
        if ((this.subdomainItems[i].url).includes(this.subdomainItems[i].tld)) {
          this.subdomainItems[i].url = (this.subdomainItems[i].url).replace(`.${this.subdomainItems[i].tld}`, '');
        }
      }
    },
    onSaveClick() {
      let addSubdomain = true;
      let addHost = true;
      const addCsv = this.csvFile !== null;
      this.subdomainItems.forEach(item => {
        if (item?.url === '') {
          addSubdomain = false;
        }
      });
      this.hostItems.forEach(item => {
        if (item?.url === '') {
          addHost = false;
        }
      });
      if (addSubdomain) {
        this.$emit('add', { items: this.subdomainItems, configType: CONFIGURATION.CONFIG_TYPES.SUBDOMAIN });
      }
      if (addHost) {
        this.$emit('add', { items: this.hostItems, configType: CONFIGURATION.CONFIG_TYPES.HOST, runIpResolver: this.runIpResolver });
      }
      if (addCsv) {
        this.$emit('upload', {
          items: this.csvFile, configType: CONFIGURATION.CONFIG_TYPES.CSV, tld: this.tldForCsv, runIpResolver: this.runIpResolverForCSV,
        });
      }
      this.onCloseClick();
    },
    onClickNext() {
      this.formattedTitle = this.$t('configuration.infrastructure.add.asset.title.summary');
      this.activeTab = this.summaryTab.label;
      if (this.isSummaryTab) {
        for (let i = 0; i < this.subdomainItems.length; i += 1) {
          if (!(this.subdomainItems[i].url).includes(this.subdomainItems[i].tld)) {
            this.subdomainItems[i].url = this.urlForSubdomain[i];
          }
        }
      }
    },
    addItem() {
      if (this.isSubdomainTab) {
        this.subdomainItems.push({ url: '', tld: '', subdomain: '' });
      } else {
        this.hostItems.push({
          url: '', tld: '', subdomain: '', subdomainsOptions: [],
        });
      }
    },
    deleteCurrentItem(index) {
      if (this.isSubdomainTab) {
        if (this.subdomainItems.length > 1) {
          this.subdomainItems.splice(index, 1);
        }
      } else if (this.hostItems.length > 1) {
        this.hostItems.splice(index, 1);
      }
    },
    async downloadCSVReport() {
      const csvContent = await this.getStreamData(); // add getStreamData
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'Example-CSV.csv');
      document.body.appendChild(link);
      link.click();
    },
    async getStreamData() {
      const rows = [];
      rows.push([this.$t('configuration.infrastructure.add.asset.csv.format.ip')]);
      rows.push([this.$t('configuration.infrastructure.add.asset.csv.format.ip.subdomain')]);
      rows.push([this.$t('configuration.infrastructure.add.asset.csv.format.ip.range')]);
      rows.push([this.$t('configuration.infrastructure.add.asset.csv.format.ip.range.subdomain')]);
      const csvContent = `data:text/csv;charset=utf-8,${
        rows.map(e => e.join(',')).join('\n')}`;
      return csvContent;
    },
  },
};
</script>

<style lang='scss'>
@import '../../assets/styles/design-system';

.add-asset-popup {
  .v-icon.v-icon {
    color: var(--text-secondary);
    font-size: $font-size-xl;
    font-weight: lighter;
  }
  .v-alert__icon.v-icon {
    color: #009688; // Teal color for info alerts
  }
  .v-autocomplete__content.v-menu__content {
    width: rem(243);
  }
  .v-menu__content .v-list-item__title {
    text-transform: unset;
    font-size: $font-size-md;
  }
  .v-input,
  .v-label,
  .v-text-field input,
  .v-select__selection {
    color: var(--text-primary) !important;
  }
  // CSV file input label should be muted gray
  .add-asset-popup__csv__selection__input-csv .v-label {
    color: var(--text-muted) !important;
  }
  .v-text-field input::placeholder,
  .v-select .v-input__slot input::placeholder {
    color: var(--text-tertiary) !important;
  }
  .v-input__slot fieldset {
    border-color: var(--border-primary) !important;
  }
  .v-input__slot {
    background-color: var(--bg-primary) !important;
  }
  // Radio button tabs - use theme-aware colors
  .el-radio-button__inner {
    background-color: var(--bg-secondary) !important;
    color: var(--text-secondary) !important;
    border-color: var(--border-primary) !important;
  }
  .el-radio-button__orig-radio:checked + .el-radio-button__inner {
    color: var(--text-inverse) !important;
    background-color: var(--text-secondary) !important;
    border-color: var(--text-secondary) !important;
    box-shadow: none !important;
  }
  // Remove focus outline on tabs
  .el-radio-button:focus:not(.is-focus):not(:active):not(.is-disabled) {
    box-shadow: none !important;
  }
}
</style>
<style lang="scss" scoped>
@import '../../assets/styles/design-system';

@keyframes subdomainHintIn {
  from {
    opacity: 0;
    transform: translateY(rem(-4));
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.add-asset-popup {
  .run-ip-resolver-checkbox {
    margin-top: $space-4;
    font-family: $default-application-font-regular;
    font-size: $font-size-xs;
    letter-spacing: 0.8px;
    color: var(--text-primary);
  }
  &__csv{
    padding-top: $space-2;
    &__wrapper {
      display: flex;
      justify-content: space-between;
      &__message {
        font-size: $font-size-base;
        max-width: rem(500);
      }
    }
    .download-csv-btn {
      height: $btn-height-md;
      width: rem(78);
      margin-top: $space-1;
      outline: none;
      &:hover {
        cursor: pointer;
      }
    }
    &__selection {
      display: inline-flex;
      align-items: flex-end;
      &__input-csv {
        width: rem(280);
        margin-right: $space-5;
        .v-label {
          color: var(--text-muted) !important;
        }
      }
      &__tld {
        width: rem(292);
        .v-input {
          margin-top: 0;
        }
        &__title {
          font-family: $default-application-font-regular;
          font-size: $font-size-xs;
          letter-spacing: 0.8px;
          line-height: $font-size-xs;
          margin-bottom: $space-1;
          color: var(--text-muted);
        }
      }
    }
  }
  .error {
    background: transparent !important;
    text-transform: capitalize;
    color: var(--severity-critical) !important;
    font-size: $font-size-xs;
    font-weight: 600;
    letter-spacing: 1.12px;
    line-height: $font-size-md;
    margin-bottom: $space-2;
    margin-top: $space-2;
  }
  .subdomain-row {
    display: flex;
    flex-direction: column;
  }
  // Gradually reveal the hint by animating the grid row from 0fr to 1fr, so the
  // modal grows smoothly instead of jumping when the preview/error appears.
  .subdomain-row__hint-wrap {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows $transition-duration-normal $ease-out-strong;
    &--open {
      grid-template-rows: 1fr;
    }
  }
  .subdomain-row__hint {
    overflow: hidden;
    min-height: 0;
    padding-left: rem(2);
    font-size: $font-size-sm;
    letter-spacing: 0.2px;
    &--preview,
    &--error {
      display: inline-flex;
      align-items: center;
      gap: rem(6);
      padding: rem(2) 0 $space-3;
      animation: subdomainHintIn $transition-duration-fast $ease-out-strong;
    }
    &--preview {
      color: var(--text-secondary);
    }
    &--error {
      color: var(--severity-critical);
      font-weight: 600;
    }
  }
  .subdomain-row__hint-icon {
    font-size: $font-size-md;
  }
  .subdomain-row__hint--preview .subdomain-row__hint-icon {
    color: var(--brand-primary);
  }
  .subdomain-row__hint--error .subdomain-row__hint-icon {
    color: var(--severity-critical);
  }
  .subdomain-row__hint-asset {
    color: var(--brand-primary);
    font-weight: 700;
    word-break: break-all;
  }
  // Press feedback + slightly softer corners for a more modern feel.
  .back-button,
  .next-button,
  .save-button {
    border-radius: $radius-6 !important;
    transition: transform $transition-duration-fast $ease-out-strong;
    &:active {
      transform: scale(0.97);
    }
  }
  .add-new-item-add-button {
    transition: transform $transition-duration-fast $ease-out-strong;
    &:active {
      transform: scale(0.97);
    }
  }
  .item-inputs {
    flex: 50%;
    display: flex;
    align-items: flex-end;
    .text-field-section{
      flex: 0 0 rem(252);
      margin-right: 2%;
      &.hosts {
        max-width: rem(280);
      }
    }
    .text-field-section.hosts ::placeholder {
      font-size: $font-size-xs;
    }
    .dot-for-subdomain {
      max-width: rem(1);
      margin-left: rem(-8);
      margin-right: $space-2;
      margin-bottom: rem(2);
      font-size: $font-size-xl;
      font-weight: bold;
    }
    .choose-subdomain-row-section {
      .v-input {
        margin-top: 0;
      }
      &__title {
        font-family: $default-application-font-regular;
        font-size: $font-size-xs;
        letter-spacing: 0.8px;
        line-height: $font-size-xs;
        margin-bottom: $space-1;
        color: #666;
      }
    }
    .choose-tld-row-section-for-subdomains {
      .v-input {
        margin-top: 0;
      }
      &__title {
        font-family: $default-application-font-regular;
        font-size: $font-size-xs;
        letter-spacing: 0.8px;
        line-height: $font-size-xs;
        margin-bottom: $space-1;
        color: #666;
      }
    }
    .choose-tld-row-section-for-hosts {
      margin-right: 2%;
      .v-input {
        margin-top: 0;
      }
      &__title {
        font-family: $default-application-font-regular;
        font-size: $font-size-xs;
        letter-spacing: 0.8px;
        line-height: $font-size-xs;
        margin-bottom: $space-1;
        color: #666;
      }
    }
    .delete-row-section{
      flex: 1;
      display: flex;
      align-items: center;
      margin-bottom: rem(8);
      &__tooltip {
        background-color: transparent;
      }
    }
    .domain-select {
      flex: 1;
      max-width: 20%;
    }
    .delete-icon {
      color: var(--severity-critical);
      cursor: pointer;
      font-size: $font-size-md;
      margin-left: $space-1;
      margin-bottom: $space-1;
      font-weight: bolder;
    }

    .dummy-icon {
      width: $font-size-md;
      height: $font-size-md;
    }
  }
  .add-new-item-section {
    padding: 0;
    .add-new-item-header {
      display: flex;
      justify-content: space-between;

      .add-new-item-add-button {
        color: var(--brand-primary);
        text-transform: uppercase;
        font-size: $font-size-sm;
        font-weight: 500;
        letter-spacing: 1.07px;
        line-height: $font-size-md;
        text-align: center;
        margin-right: $space-3;
        cursor: pointer;

        .plus {
          font-size: $font-size-md;
        }
      }
    }
  }
}
.add-asset-popup {
  .popup-wrapper {
    max-width: rem(1000);
    padding: $space-8 $space-10;
    background-color: var(--bg-secondary);
    color: var(--text-primary);
    border-radius: $radius-2xl;
    box-shadow: $shadow-2xl;

    .escape-link {
      float: right;
      cursor: pointer;
      margin-left: $space-5;
      text-align: right;
      font-size: $font-size-sm;
      text-transform: uppercase;
      text-decoration: none;
      height: $font-size-md;
      color: var(--brand-primary);
      font-family: $default-application-font-medium;
      font-weight: 500;
      letter-spacing: 1.1px;
      line-height: $font-size-md;
    }
    .content {
      .add-item-form {
        margin-top: $space-2;
      }
      .add-title {
        text-transform: uppercase;
        color: var(--text-primary);
        font-size: $font-size-base;
        font-weight: 600;
        letter-spacing: 1.12px;
        line-height: $font-size-md;
        margin-bottom: $space-4;
      }
      .summary-items {
        margin-top: $space-5;
        display: flex;
        flex-direction: column;
        padding-right: $space-5;
        .summary-item-row {
          width: 80%;
          line-height: 2.5;
          display: flex;
          justify-content: space-between;
          flex: 1;
          align-items: center;
          color: var(--text-primary);
          .row-title {
            min-width: 30%;
            font-family: $default-application-font-medium;
            font-size: $font-size-md;
            color: var(--text-secondary);
          }
          .row-value {
            min-width: 30%;
            font-family: $default-application-font-regular;
            font-size: $font-size-base;
            color: var(--text-primary);
          }
        }
        .title-divider {
          opacity: 0.5;
          min-width: 80%;
        }
        .buttons {
          text-align: right;
          padding-top: $space-8;
          padding-right: $space-5;

          .back-button {
            height: $btn-height-md;
            border-radius: $radius-sm;
          }

          .save-button {
            height: $btn-height-md;
            border-radius: $radius-sm;
            margin-left: $space-2;
          }
        }
      }
    }
    .buttons {
      text-align: right;
      padding-top: $space-8;
      padding-right: $space-5;

      .back-button {
        height: $btn-height-md;
        border-radius: $radius-sm;
      }

      .next-button {
        height: $btn-height-md;
        border-radius: $radius-sm;
        margin-left: $space-2;
      }

      .save-button {
        height: $btn-height-md;
        border-radius: $radius-sm;
        margin-left: $space-2;
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .add-asset-popup {
    .subdomain-row__hint-wrap {
      transition: none;
    }
    .subdomain-row__hint--preview,
    .subdomain-row__hint--error {
      animation: none;
    }
    .back-button,
    .next-button,
    .save-button,
    .add-new-item-add-button {
      transition: none;
      &:active {
        transform: none;
      }
    }
  }
}
</style>
<style lang="scss">
.v-dialog__content--active {
  .add-new-item-popup {
    overflow-y: auto;
  }
}
</style>
